import "../style/ManageGardenPlots.css";
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Box, Typography, CircularProgress, Button, IconButton, Tooltip } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowBack, Home, Park, Delete, Yard, TextFields } from "@mui/icons-material";
import apiClient from "../utils/apiClient";
import PlotForm from "../components/PlotForm";
import PinchZoomResponder from "../utils/pinchZoomResponder";

const ManageGardenPlots = () => {
  const { garden_uuid } = useParams();
  const navigate = useNavigate();
  const viewportRef = useRef(null);
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const [garden, setGarden] = useState(null);
  const [plots, setPlots] = useState([]);
  const [plotTypes, setPlotTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  // camera + interactions
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [hoveredPlot, setHoveredPlot] = useState(null);
  const [draggingPlot, setDraggingPlot] = useState(null);
  const [clickedPlot, setClickedPlot] = useState(null); // Track clicked plot (including locked ones)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState(null);
  const [isCanvasFocused, setIsCanvasFocused] = useState(false);
  const [isMouseOverCanvas, setIsMouseOverCanvas] = useState(false);
  // Pinch zoom state - use refs for synchronous access during touch events
  const pinchStartRef = useRef(null);
  const pinchStartScaleRef = useRef(1);
  const pinchStartOffsetRef = useRef({ x: 0, y: 0 });
  const scaleRef = useRef(1);
  const offsetRef = useRef({ x: 0, y: 0 });

  // sidebar
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedPlot, setSelectedPlot] = useState(null);
  const [showPlotLabels, setShowPlotLabels] = useState(true);

  const soilTypes = ["loam", "clay", "sand", "silt"];

  // Mobile detection and sidebar state
  useEffect(() => {
    const checkMobile = () => {
      // Check for mobile device: either width < 768 OR height < 768 (landscape mode)
      const isMobileWidth = window.innerWidth < 768;
      const isMobileHeight = window.innerHeight < 768;
      // Also check for touch capability
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      setIsMobile(isMobileWidth || isMobileHeight || (hasTouch && (window.innerWidth < 1024 || window.innerHeight < 768)));
    };
    
    const checkSidebarState = () => {
      const saved = localStorage.getItem('sidebarCollapsed');
      setIsSidebarCollapsed(saved ? JSON.parse(saved) : false);
    };
    
    checkMobile();
    checkSidebarState();
    
    // Add class to body to hide TopBar border
    document.body.classList.add('on-manage-garden-plots');
    
    window.addEventListener("resize", checkMobile);
    window.addEventListener("storage", checkSidebarState);
    
    // Listen for sidebar collapse changes via custom event
    const handleSidebarChange = (event) => {
      if (event.detail && event.detail.collapsed !== undefined) {
        setIsSidebarCollapsed(event.detail.collapsed);
      } else {
        checkSidebarState();
      }
    };
    window.addEventListener('sidebarCollapseChange', handleSidebarChange);
    
    // Also poll localStorage periodically as fallback
    const interval = setInterval(checkSidebarState, 500);
    
    return () => {
      document.body.classList.remove('on-manage-garden-plots');
      window.removeEventListener("resize", checkMobile);
      window.removeEventListener("storage", checkSidebarState);
      window.removeEventListener('sidebarCollapseChange', handleSidebarChange);
      clearInterval(interval);
    };
  }, []);

  // ------------------------------------------------------------
  // fetch data
  const normalizePlot = useCallback((p) => ({
    ...p,
    x: Number(p.x),
    y: Number(p.y),
    width: Number(p.width),
    depth: Number(p.depth),
    rotation: Number(p.rotation ?? 0),
    locked: Boolean(p.locked),
  }), []);

  useEffect(() => {
    const fetchGardenAndPlots = async () => {
      try {
        const [gardenRes, plotsRes, plotTypesRes] = await Promise.all([
          apiClient.get(`/gardens/${garden_uuid}`),
          apiClient.get(`/gardens/${garden_uuid}/plots`),
          apiClient.get(`/plottypes`)
        ]);
        setGarden(gardenRes.data);
        setPlots((plotsRes.data || []).map((p) => normalizePlot(p)));
        setPlotTypes(plotTypesRes.data);
      } catch (err) {
        console.error("Failed to fetch:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchGardenAndPlots();
  }, [garden_uuid]);

  // Keep refs in sync with state
  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);

  // Initialize PinchZoomResponder
  const pinchZoomResponder = useMemo(() => {
    return new PinchZoomResponder({
      onPinchZoomStart: (e) => {
        e.preventDefault();
        setIsCanvasFocused(true);
        setIsMouseOverCanvas(true);
        setIsPanning(false);
        setDraggingPlot(null);
        setClickedPlot(null);
      },
      onPinchZoomEnd: (e) => {
        pinchStartRef.current = null;
        pinchStartScaleRef.current = 1;
        pinchStartOffsetRef.current = { x: 0, y: 0 };
        if (e.touches.length === 0) {
          setIsMouseOverCanvas(false);
          setTimeout(() => setIsCanvasFocused(false), 100);
        }
      },
      onResponderMove: (e, gestureState) => {
        if (!gestureState) return;
        
        e.preventDefault();
        
        // Get the center point relative to the canvas
        const rect = viewportRef.current?.getBoundingClientRect();
        if (!rect) return;
        
        const centerX = gestureState.cx - rect.left;
        const centerY = gestureState.cy - rect.top;
        
        // Initialize pinch start if not already set
        if (!pinchStartRef.current) {
          pinchStartRef.current = {
            distance: Math.hypot(gestureState.dx, gestureState.dy) / gestureState.scaleX,
            center: { x: centerX, y: centerY }
          };
          pinchStartScaleRef.current = scaleRef.current;
          pinchStartOffsetRef.current = { ...offsetRef.current };
        }
        
        // Calculate new scale
        const scaleFactor = gestureState.scaleX;
        const newScale = Math.max(0.1, Math.min(10, pinchStartScaleRef.current * scaleFactor));
        
        // Convert center point to world coordinates using the start state
        const worldBefore = {
          x: (pinchStartRef.current.center.x - pinchStartOffsetRef.current.x) / pinchStartScaleRef.current,
          y: (pinchStartRef.current.center.y - pinchStartOffsetRef.current.y) / pinchStartScaleRef.current
        };
        
        // Calculate new offset so that the world point stays under the center
        const newOffset = {
          x: centerX - worldBefore.x * newScale,
          y: centerY - worldBefore.y * newScale
        };
        
        setScale(newScale);
        setOffset(newOffset);
      }
    });
  }, []);

  // ------------------------------------------------------------
  // coordinate helpers
  const worldToScreen = (x, y) => ({
    x: x * scaleRef.current + offsetRef.current.x,
    y: y * scaleRef.current + offsetRef.current.y,
  });

  const screenToWorld = (x, y) => ({
    x: (x - offsetRef.current.x) / scaleRef.current,
    y: (y - offsetRef.current.y) / scaleRef.current,
  });

  const fitToView = (viewportEl, garden) => {
    if (!viewportEl || !garden) return;
    const padding = 0.1;
    const cw = viewportEl.clientWidth;
    const ch = viewportEl.clientHeight;
    const gardenW = Math.max(garden.width, garden.depth);
    const gardenH = Math.min(garden.width, garden.depth);
    const scaleX = (cw * (1 - padding)) / gardenW;
    const scaleY = (ch * (1 - padding)) / gardenH;
    const newScale = Math.min(scaleX, scaleY);
    const offsetX = (cw - gardenW * newScale) / 2;
    const offsetY = (ch - gardenH * newScale) / 2;
    setScale(newScale);
    setOffset({ x: offsetX, y: offsetY });
  };

  // ------------------------------------------------------------
  // form logic
  const handleSidebarClose = () => {
    setSelectedPlot(null);
    setIsSidebarOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const numericFields = new Set(["width", "depth", "x", "y", "rotation"]);
    setSelectedPlot((prev) => ({
      ...prev,
      [name]: numericFields.has(name) ? Number(value) : value,
    }));
  };

  const cleanPlotPayload = (p) => ({
    name: p.name,
    plot_type_uuid: p.plot_type_uuid,
    shape: p.shape,
    description: p.description,
    width: p.width,
    depth: p.depth,
    x: p.x,
    y: p.y,
    rotation: p.rotation ?? 0.0,
    unit: p.unit,
    garden_uuid: p.garden_uuid,
    soil_type_uuid: p.soil_type_uuid ?? null,
    plot_group_uuid: p.plot_group_uuid ?? null,
    locked: p.locked ?? false,
  });

  const handlePlotUpdate = async () => {
    try {
      if (!selectedPlot?.plot_uuid) {
        console.error("No plot_uuid found in selectedPlot!");
        return;
      }
      
      const normalized = normalizePlot(selectedPlot);
      const payload = cleanPlotPayload(normalized);
      const response = await apiClient.put(`/plots/${selectedPlot.plot_uuid}`, payload);
      const updatedPlot = normalizePlot(response.data);
      
      setPlots((prev) =>
        prev.map((pl) =>
          pl.plot_uuid === selectedPlot.plot_uuid ? updatedPlot : pl
        )
      );
      
      setSelectedPlot(updatedPlot);
      handleSidebarClose();
    } catch (err) {
      console.error("Failed to update plot:", err);
    }
  };

  const handlePlotDelete = async () => {
    try {
      await apiClient.delete(`/plots/${selectedPlot.plot_uuid}`);
      setPlots((prev) =>
        prev.filter((p) => p.plot_uuid !== selectedPlot.plot_uuid)
      );
      handleSidebarClose();
    } catch (err) {
      console.error("Failed to delete plot:", err);
    }
  };

  const handlePlotAdd = async (plotTypeName, shape) => {
    try {
      const gardenW = Math.max(garden.width, garden.depth);
      const gardenH = Math.min(garden.width, garden.depth);
      
      // Find the plot type UUID based on the name
      const plotType = plotTypes.find(pt => pt.name.toLowerCase().replace(/\s+/g, '-') === plotTypeName);
      const plotTypeUuid = plotType?.plot_type_uuid || plotTypes[0]?.plot_type_uuid || "";
      
      // Set default names and dimensions based on plot type
      let defaultName, defaultWidth, defaultDepth;
      switch(plotTypeName) {
        case "vegetable-bed":
          defaultName = "New Vegetable Plot";
          defaultWidth = 4;
          defaultDepth = 2;
          break;
        case "fruit-tree":
          defaultName = "New Tree";
          defaultWidth = 2;
          defaultDepth = 2;
          break;
        case "visual-element":
          defaultName = "New Visual Element";
          defaultWidth = 2;
          defaultDepth = 1;
          break;
        case "compost-bin":
          defaultName = "New Compost Bin";
          defaultWidth = 1;
          defaultDepth = 1;
          break;
        default:
          defaultName = "New Plot";
          defaultWidth = 4;
          defaultDepth = 2;
      }
      
      const newPlot = {
        name: defaultName,
        plot_type_uuid: plotTypeUuid,
        shape: shape,
        description: "",
        soil_type_uuid: null,
        width: defaultWidth,
        depth: defaultDepth,
        x: gardenW / 2 - defaultWidth / 2,
        y: gardenH / 2 - defaultDepth / 2,
        rotation: 0.0,
        unit: garden?.unit || "meters",
        garden_uuid: garden.garden_uuid,
        locked: false,
      };
      const res = await apiClient.post("/plots", newPlot);
      setPlots((prev) => [...prev, normalizePlot(res.data)]);
    } catch (err) {
      console.error("Failed to add plot:", err);
    }
  };

  // ------------------------------------------------------------
  // fit and resize handling for div-based viewport
  useEffect(() => {
    if (!garden || !viewportRef.current) return;
    fitToView(viewportRef.current, garden);
  }, [garden]);

  useEffect(() => {
    const handleResize = () => {
      if (!viewportRef.current || !garden) return;
      fitToView(viewportRef.current, garden);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [garden]);

  // Global scroll prevention when mouse is over canvas
  useEffect(() => {
    const preventScroll = (e) => {
      // Don't prevent on the canvas wrapper itself - let its handlers deal with it
      if (viewportRef.current && viewportRef.current.contains(e.target)) {
        return;
      }
      if (isMouseOverCanvas || isPanning || draggingPlot || pinchStartRef.current) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    const preventKeyScroll = (e) => {
      // Never block typing keys inside editable controls (form fields, etc.)
      const target = e.target;
      const isEditableTarget =
        target instanceof HTMLElement &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable);
      if (isEditableTarget) return;

      if (isMouseOverCanvas || isPanning || draggingPlot || pinchStartRef.current) {
        // Prevent arrow keys and space from scrolling
        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' ', 'PageUp', 'PageDown', 'Home', 'End'].includes(e.key)) {
          e.preventDefault();
        }
      }
    };

    // Always add the listeners with passive: false to allow preventDefault
    document.addEventListener('wheel', preventScroll, { passive: false });
    document.addEventListener('touchmove', preventScroll, { passive: false });
    document.addEventListener('keydown', preventKeyScroll, { passive: false });

    return () => {
      document.removeEventListener('wheel', preventScroll);
      document.removeEventListener('touchmove', preventScroll);
      document.removeEventListener('keydown', preventKeyScroll);
    };
  }, [isMouseOverCanvas, isPanning, draggingPlot]);

  // Track mouse position globally to detect when over canvas
  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      if (!viewportRef.current) return;
      
      const rect = viewportRef.current.getBoundingClientRect();
      const isOverCanvas = (
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom
      );
      
      setIsMouseOverCanvas(isOverCanvas);
    };

    document.addEventListener('mousemove', handleGlobalMouseMove);
    return () => document.removeEventListener('mousemove', handleGlobalMouseMove);
  }, []);

  // ------------------------------------------------------------
  // ------------------------------------------------------------
  // interactions on viewport div
  const getMouse = useCallback((e) => {
    const rect = viewportRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }, []);

  const getTouch = useCallback((e) => {
    const rect = viewportRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const touch = e.touches && e.touches.length > 0 
      ? e.touches[0] 
      : (e.changedTouches && e.changedTouches.length > 0 ? e.changedTouches[0] : null);
    if (!touch) return { x: 0, y: 0 };
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  }, []);

  // Calculate distance between two touches
  const getTouchDistance = useCallback((touch1, touch2) => {
    if (!touch1 || !touch2) return 0;
    const rect = viewportRef.current?.getBoundingClientRect();
    if (!rect) return 0;
    const x1 = touch1.clientX - rect.left;
    const y1 = touch1.clientY - rect.top;
    const x2 = touch2.clientX - rect.left;
    const y2 = touch2.clientY - rect.top;
    return Math.hypot(x2 - x1, y2 - y1);
  }, []);

  // Get center point between two touches
  const getTouchCenter = useCallback((touch1, touch2) => {
    if (!touch1 || !touch2) return { x: 0, y: 0 };
    const rect = viewportRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const x1 = touch1.clientX - rect.left;
    const y1 = touch1.clientY - rect.top;
    const x2 = touch2.clientX - rect.left;
    const y2 = touch2.clientY - rect.top;
    return { x: (x1 + x2) / 2, y: (y1 + y2) / 2 };
  }, []);

  const handleTouchStart = useCallback((e) => {
    e.preventDefault();
    setIsCanvasFocused(true);
    setIsMouseOverCanvas(true);

    // Check for pinch zoom (two touches) - this takes priority
    if (e.touches.length === 2) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = getTouchDistance(touch1, touch2);
      const center = getTouchCenter(touch1, touch2);
      
      // Use refs for immediate synchronous access
      pinchStartRef.current = { distance, center };
      pinchStartScaleRef.current = scaleRef.current;
      pinchStartOffsetRef.current = { ...offsetRef.current };
      
      setIsPanning(false);
      setDraggingPlot(null);
      setClickedPlot(null);
      setDragStart(null);
      return;
    }

    // Single touch - handle normal interactions
    // Only proceed if we're not in the middle of a pinch (check ref for immediate value)
    if (e.touches.length === 1 && !pinchStartRef.current) {
      const { x, y } = getTouch(e);
      const world = screenToWorld(x, y);
      const hit = plots.find(
        (p) =>
          world.x >= p.x &&
          world.x <= p.x + p.width &&
          world.y >= p.y &&
          world.y <= p.y + p.depth
      );
      setDragStart({ x, y });

      if (hit && !hit.locked) {
        // Only allow dragging if plot is not locked
        setDraggingPlot(hit);
        setClickedPlot(hit);
        const plotScreen = worldToScreen(hit.x, hit.y);
        setDragOffset({ x: x - plotScreen.x, y: y - plotScreen.y });
      } else if (hit && hit.locked) {
        // Track locked plot for tap detection
        setClickedPlot(hit);
      } else if (!hit) {
        setIsPanning(true);
        setPanStart({ x: x - offsetRef.current.x, y: y - offsetRef.current.y });
      }
    }
  }, [getTouch, plots, screenToWorld, worldToScreen, getTouchDistance, getTouchCenter]);

  const handleTouchMove = useCallback((e) => {
    e.preventDefault();

    // Handle pinch zoom (two touches) - check for transition from 1 to 2 touches
    if (e.touches.length === 2) {
      // If we weren't pinching before, start now (check ref for immediate value)
      if (!pinchStartRef.current) {
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const distance = getTouchDistance(touch1, touch2);
        const center = getTouchCenter(touch1, touch2);
        
        // Use refs for immediate synchronous access
        pinchStartRef.current = { distance, center };
        pinchStartScaleRef.current = scaleRef.current;
        pinchStartOffsetRef.current = { ...offsetRef.current };
        
        setIsPanning(false);
        setDraggingPlot(null);
        return;
      }
      
      // Continue pinch zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = getTouchDistance(touch1, touch2);
      const center = getTouchCenter(touch1, touch2);
      
      const pinchData = pinchStartRef.current;
      if (pinchData && pinchData.distance > 0) {
        // Calculate scale factor based on distance change
        const scaleFactor = distance / pinchData.distance;
        const newScale = Math.max(0.1, Math.min(10, pinchStartScaleRef.current * scaleFactor));
        
        // Zoom around the center point
        // Convert center point to world coordinates using the start state
        const worldBefore = {
          x: (pinchData.center.x - pinchStartOffsetRef.current.x) / pinchStartScaleRef.current,
          y: (pinchData.center.y - pinchStartOffsetRef.current.y) / pinchStartScaleRef.current
        };
        // Calculate new offset so that the world point stays under the center
        const newOffset = {
          x: center.x - worldBefore.x * newScale,
          y: center.y - worldBefore.y * newScale
        };
        
        setScale(newScale);
        setOffset(newOffset);
      }
      return;
    }

    // Single touch - handle normal interactions (only if not ending a pinch)
    if (e.touches.length === 1 && !pinchStartRef.current) {
      const { x, y } = getTouch(e);
      if (isPanning) {
        setOffset({ x: x - panStart.x, y: y - panStart.y });
        return;
      }
      if (draggingPlot) {
        const { x: wx, y: wy } = screenToWorld(x - dragOffset.x, y - dragOffset.y);
        setPlots((prev) =>
          prev.map((p) =>
            p.plot_uuid === draggingPlot.plot_uuid ? { ...p, x: wx, y: wy } : p
          )
        );
      }
    }
  }, [getTouch, isPanning, panStart, draggingPlot, dragOffset, screenToWorld, getTouchDistance, getTouchCenter]);

  const handleTouchEnd = useCallback(async (e) => {
    e.preventDefault();

    // Reset pinch zoom state when we go below 2 touches (check ref for immediate value)
    if (e.touches.length < 2 && pinchStartRef.current) {
      pinchStartRef.current = null;
      pinchStartScaleRef.current = 1;
      pinchStartOffsetRef.current = { x: 0, y: 0 };
      // Don't handle touch end actions if we just finished a pinch
      if (e.touches.length === 0) {
        setIsMouseOverCanvas(false);
        setTimeout(() => setIsCanvasFocused(false), 100);
      }
      return;
    }

    // Handle single touch end (only if we weren't pinching)
    if (e.touches.length === 0 && !pinchStartRef.current) {
      const { x, y } = getTouch(e);
      const moved = dragStart && Math.hypot(x - dragStart.x, y - dragStart.y) > 5;

      if (draggingPlot) {
        if (moved) {
          try {
            const p = plots.find((pp) => pp.plot_uuid === draggingPlot.plot_uuid);
            if (p) await apiClient.put(`/plots/${p.plot_uuid}`, cleanPlotPayload(p));
          } catch (err) {
            console.error("Failed to save moved plot:", err);
          }
        } else {
          // Tap on plot - open modal
          setSelectedPlot(draggingPlot);
          setIsSidebarOpen(true);
        }
      } else if (!moved && clickedPlot) {
        // Tap on plot (including locked plots) - open modal
        setSelectedPlot(clickedPlot);
        setIsSidebarOpen(true);
      } else if (!moved) {
        // Fallback: Check if we tapped on a plot
        const world = screenToWorld(x, y);
        const hit = plots.find(
          (p) =>
            world.x >= p.x &&
            world.x <= p.x + p.width &&
            world.y >= p.y &&
            world.y <= p.y + p.depth
        );
        if (hit) {
          setSelectedPlot(hit);
          setIsSidebarOpen(true);
        }
      }

      setDraggingPlot(null);
      setClickedPlot(null);
      setIsPanning(false);
      setDragStart(null);
      setIsMouseOverCanvas(false);
      setTimeout(() => setIsCanvasFocused(false), 100);
    }
  }, [getTouch, dragStart, draggingPlot, clickedPlot, plots, screenToWorld]);

  // Add direct wheel event listener to canvas with passive: false
  useEffect(() => {
    const canvasElement = viewportRef.current;
    if (!canvasElement) return;

    const handleCanvasWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const { x, y } = getMouse(e);
      const zoom = e.deltaY < 0 ? 1.1 : 0.9;
      const worldBefore = screenToWorld(x, y);
      const newScale = scale * zoom;
      setScale(newScale);
      setOffset({
        x: x - worldBefore.x * newScale,
        y: y - worldBefore.y * newScale,
      });
    };

    canvasElement.addEventListener('wheel', handleCanvasWheel, { passive: false });
    return () => canvasElement.removeEventListener('wheel', handleCanvasWheel);
  }, [getMouse, scale, screenToWorld]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const { x, y } = getMouse(e);
    const zoom = e.deltaY < 0 ? 1.1 : 0.9;
    const worldBefore = screenToWorld(x, y);
    const newScale = scale * zoom;
    setScale(newScale);
    setOffset({
      x: x - worldBefore.x * newScale,
      y: y - worldBefore.y * newScale,
    });
  }, [getMouse, scale, screenToWorld]);

  const handleMouseDown = useCallback((e) => {
    setIsCanvasFocused(true);
    const { x, y } = getMouse(e);
    const world = screenToWorld(x, y);
    const hit = plots.find(
      (p) =>
        world.x >= p.x &&
        world.x <= p.x + p.width &&
        world.y >= p.y &&
        world.y <= p.y + p.depth
    );
    setDragStart({ x, y });

    if (hit) {
      // Track which plot was clicked (including locked ones)
      setClickedPlot(hit);
      if (!hit.locked) {
        // Only allow dragging if plot is not locked
        setDraggingPlot(hit);
        const plotScreen = worldToScreen(hit.x, hit.y);
        setDragOffset({ x: x - plotScreen.x, y: y - plotScreen.y });
      }
    } else {
      setClickedPlot(null);
      setIsPanning(true);
      setPanStart({ x: x - offset.x, y: y - offset.y });
    }
  }, [getMouse, plots, offset, screenToWorld, worldToScreen]);

  const handleMouseMove = useCallback((e) => {
    const { x, y } = getMouse(e);
    if (isPanning) {
      setOffset({ x: x - panStart.x, y: y - panStart.y });
      return;
    }
    if (draggingPlot) {
      const { x: wx, y: wy } = screenToWorld(x - dragOffset.x, y - dragOffset.y);
      setPlots((prev) =>
        prev.map((p) =>
          p.plot_uuid === draggingPlot.plot_uuid ? { ...p, x: wx, y: wy } : p
        )
      );
      return;
    }
    const hovered = plots.find((p) => {
      const { x: px, y: py } = worldToScreen(p.x, p.y);
      const pw = p.width * scale;
      const ph = p.depth * scale;
      return x >= px && x <= px + pw && y >= py && y <= py + ph;
    });
    setHoveredPlot(hovered || null);
  }, [getMouse, isPanning, panStart, draggingPlot, dragOffset, plots, scale, screenToWorld, worldToScreen]);

  const handleMouseUp = useCallback(async (e) => {
    const { x, y } = getMouse(e);
    const moved = dragStart && Math.hypot(x - dragStart.x, y - dragStart.y) > 5;

    if (draggingPlot) {
      if (moved) {
        try {
          const p = plots.find((pp) => pp.plot_uuid === draggingPlot.plot_uuid);
          if (p) await apiClient.put(`/plots/${p.plot_uuid}`, cleanPlotPayload(p));
        } catch (err) {
          console.error("Failed to save moved plot:", err);
        }
      } else {
        // Click/tap on plot - open modal
        setSelectedPlot(draggingPlot);
        setIsSidebarOpen(true);
      }
    } else if (!moved && clickedPlot) {
      // Click on plot (including locked plots) - open modal
      setSelectedPlot(clickedPlot);
      setIsSidebarOpen(true);
    } else if (!moved && hoveredPlot) {
      // Fallback: use hovered plot
      setSelectedPlot(hoveredPlot);
      setIsSidebarOpen(true);
    }

    setDraggingPlot(null);
    setClickedPlot(null);
    setIsPanning(false);
    setDragStart(null);
    
    // Keep canvas focused for a short time to prevent immediate page scroll
    setTimeout(() => setIsCanvasFocused(false), 100);
  }, [getMouse, dragStart, draggingPlot, clickedPlot, hoveredPlot, plots]);

  // ------------------------------------------------------------
  // growth stage helper for CSS classes
  const getGrowthStage = useCallback((planted) => {
    if (!planted) return null;
    const today = new Date();
    const msPerDay = 1000 * 60 * 60 * 24;
    const plantedDate = planted.date_planted ? new Date(planted.date_planted) : null;
    const daysSince = plantedDate ? Math.max(0, Math.floor((today - plantedDate) / msPerDay)) : 0;

    const rawMin = planted?.crop?.harvest_min_days;
    const rawMax = planted?.crop?.harvest_max_days;
    const minDays = Number.isFinite(Number(rawMin)) && Number(rawMin) > 0 ? Number(rawMin) : 21; // default 3 weeks
    const maxDays = Number.isFinite(Number(rawMax)) && Number(rawMax) > 0 ? Number(rawMax) : minDays + 14; // default +2 weeks

    if (daysSince < 0.25 * minDays) return "sprouting";
    if (daysSince < minDays) return "growing";
    if (daysSince <= maxDays) return "harvest";
    return "harvest"; // fallback: still show harvest icon after window
  }, []);


  // ------------------------------------------------------------
  if (loading) return <CircularProgress />;
  if (!garden) return <Typography color="error">Garden not found.</Typography>;


  return (
    <Box className={`manage-garden-page ${isMobile ? 'manage-garden-page-mobile' : ''} ${!isMobile && isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Floating Sidebar Overlay */}
      <Box className="manage-garden-toolbar toolbar-sidebar">
        <Tooltip title="Back" placement="right">
          <IconButton
            onClick={() => navigate(`/gardens/${garden_uuid}`)}
            className="toolbar-back-button"
            size="small"
          >
            <ArrowBack />
          </IconButton>
        </Tooltip>
        <Box className="toolbar-buttons-vertical">
          <Tooltip title="Add Plot" placement="right">
            <IconButton
              onClick={() => handlePlotAdd("vegetable-bed", "rectangle")}
              className="toolbar-add-button"
              size="small"
            >
              <Yard />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add Tree" placement="right">
            <IconButton
              onClick={() => handlePlotAdd("fruit-tree", "circle")}
              className="toolbar-add-button"
              size="small"
            >
              <Park />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add Visual Element" placement="right">
            <IconButton
              onClick={() => handlePlotAdd("visual-element", "rectangle")}
              className="toolbar-add-button"
              size="small"
            >
              <Home />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add Compost Bin" placement="right">
            <IconButton
              onClick={() => handlePlotAdd("compost-bin", "rectangle")}
              className="toolbar-add-button"
              size="small"
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
        <Box className="toolbar-buttons-vertical" sx={{ marginTop: '1rem' }}>
          <Tooltip title={showPlotLabels ? "Hide Labels" : "Show Labels"} placement="right">
            <IconButton
              onClick={() => setShowPlotLabels(!showPlotLabels)}
              className="toolbar-add-button"
              size="small"
            >
              <TextFields />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box
        className={`canvas-wrapper ${isMobile ? 'canvas-wrapper-mobile' : ''}`}
        ref={viewportRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseEnter={() => {
          setIsCanvasFocused(true);
          setIsMouseOverCanvas(true);
        }}
        onMouseLeave={() => {
          setIsMouseOverCanvas(false);
          if (!isPanning && !draggingPlot) {
            setIsCanvasFocused(false);
          }
        }}
        onTouchStart={(e) => {
          // Let pinchZoomResponder handle touch start first
          const pinchHandlers = pinchZoomResponder.getHandlers();
          pinchHandlers.onTouchStart(e);
          
          // If not a pinch (single touch), handle normal interactions
          if (e.touches.length === 1 && !pinchStartRef.current) {
            handleTouchStart(e);
          }
        }}
        onTouchMove={(e) => {
          // Let pinchZoomResponder handle touch move first
          const pinchHandlers = pinchZoomResponder.getHandlers();
          pinchHandlers.onTouchMove(e);
          
          // If not a pinch (single touch), handle normal interactions
          if (e.touches.length === 1 && !pinchStartRef.current) {
            handleTouchMove(e);
          }
        }}
        onTouchEnd={(e) => {
          // Let pinchZoomResponder handle touch end first
          const pinchHandlers = pinchZoomResponder.getHandlers();
          pinchHandlers.onTouchEnd(e);
          
          // If not ending a pinch, handle normal interactions
          if (!pinchStartRef.current) {
            handleTouchEnd(e);
          }
        }}
        onTouchCancel={(e) => {
          const pinchHandlers = pinchZoomResponder.getHandlers();
          if (pinchHandlers.onTouchCancel) {
            pinchHandlers.onTouchCancel(e);
          }
        }}
        style={{ touchAction: 'none' }}
      >
        {(() => {
          const gardenW = Math.max(garden.width, garden.depth);
          const gardenH = Math.min(garden.width, garden.depth);
          const gardenStyle = {
            left: `${offset.x}px`,
            top: `${offset.y}px`,
            width: `${gardenW * scale}px`,
            height: `${gardenH * scale}px`,
          };

          // Calculate grid size: 1 meter = scale pixels
          const gridSize = scale;
          
          return (
            <div 
              className="garden" 
              style={{
                ...gardenStyle,
                '--grid-size': `${gridSize}px`
              }}
            >
              {plots.map((plot) => {
                const x = plot.x * scale; // relative to garden origin (no offset)
                const y = plot.y * scale; // relative to garden origin (no offset)
                const w = plot.width * scale;
                const h = plot.depth * scale;
                const isHovered = hoveredPlot?.plot_uuid === plot.plot_uuid;

                const typeVar = `--rectangle-type-${plot.type?.replace(/\s+/g, "-")}`;
                const soilVar = `--rectangle-soil-${plot.soil_type?.name ?? ""}`;
                const docStyles = getComputedStyle(document.documentElement);
                const typeColor = docStyles.getPropertyValue(typeVar);
                const soilColor = docStyles.getPropertyValue(soilVar);

                const hoverColor = docStyles.getPropertyValue("--plot-hover").trim();
                const defaultColor = docStyles.getPropertyValue("--plot-default").trim();
                // Keep growth overlay size fixed per CSS var (not zoom-dependent)

                const activePlanted = plot.current_planted || [];
                const cropTypes = activePlanted.map((p) => p.crop?.crop_type?.name?.toLowerCase()).filter(Boolean);
                const cropClassList = [...new Set(cropTypes.map((t) => `crop-${t}`))];
                const stage = activePlanted.length ? getGrowthStage(activePlanted[0]) : (cropTypes.length ? "growing" : null);
                const stageClass = stage ? `stage-${stage}` : "";

                // Generate plot type and shape classes
                const plotTypeClass = plot.plot_type?.name ? `plottype-${plot.plot_type.name.replace(/\s+/g, "-").toLowerCase()}` : "";
                const plotShapeClass = plot.shape ? `plotshape-${plot.shape.toLowerCase()}` : "";
                const oldTypeClass = plot.type ? `type-${plot.type.replace(/\s+/g, "-").toLowerCase()}` : "";

                let baseColor = (typeColor || "").trim() || defaultColor;
                if (cropClassList.length) {
                  const cropType = cropTypes[0];
                  const cropVar = `--crop-color-${cropType}`;
                  const cssColor = docStyles.getPropertyValue(cropVar);
                  if (cssColor) baseColor = cssColor.trim();
                }

                const backgroundColor = (isHovered && !plot.locked) ? hoverColor : baseColor;
                const borderColor = (soilColor || "").trim() || "var(--plot-border)";

                const plotStyle = {
                  left: `${x}px`,
                  top: `${y}px`,
                  width: `${w}px`,
                  height: `${h}px`,
                  transform: `rotate(${plot.rotation || 0}deg)`,
                  "--plot-color": backgroundColor,
                  borderColor,
                };

                const typeClass = plot.type ? `type-${plot.type.replace(/\s+/g, "-").toLowerCase()}` : "";
                const classNames = ["plot", typeClass, plotTypeClass, plotShapeClass, ...cropClassList, stageClass].filter(Boolean).join(" ");

                return (
                  <div key={plot.plot_uuid} className={classNames} style={plotStyle}>
                    {showPlotLabels && <div className="plot-label">{plot.name || "Plot"}</div>}
                    {showPlotLabels && plot.locked && <div className="lock-icon">🔒</div>}
                  </div>
                );
              })}
            </div>
          );
        })()}
      </Box>

      <PlotForm
        isOpen={isSidebarOpen}
        selectedPlot={selectedPlot}
        handleClose={handleSidebarClose}
        handleInputChange={handleInputChange}
        handlePlotUpdate={handlePlotUpdate}
        handlePlotDelete={handlePlotDelete}
        plotTypes={plotTypes}
        soilTypes={soilTypes}
        isMobile={isMobile}
      />
    </Box>
  );
};

export default ManageGardenPlots;
